import type { FC } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from '@/contexts/AuthContext';
import { Analytics } from '@vercel/analytics/react';
import { Layout } from '@/components/layout';
import { ClosedRoute } from '@/components/shared';
import { HomePage } from '@/pages/HomePage';
import { EventPage } from '@/pages/EventPage';
import { TournamentDetailPage } from '@/pages/TournamentDetailPage';
import { ActivityDetailPage } from '@/pages/ActivityDetailPage';
import { SpecialEventPage } from '@/pages/SpecialEventPage';
import { SpecialEventsPage } from '@/pages/SpecialEventsPage';
import { SpecialEventDetailPage } from '@/pages/SpecialEventDetailPage';
import { VolunteerPage } from '@/pages/VolunteerPage';
import { AboutPage } from '@/pages/AboutPage';
import { LoginPage } from '@/pages/LoginPage';
import { RegisterPage } from '@/pages/RegisterPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { DashboardPage } from '@/pages/DashboardPage';
import { TournamentRegistrationPage } from '@/pages/TournamentRegistrationPage';
import { ActivityRegistrationPage } from '@/pages/ActivityRegistrationPage';
import { ProfilePage } from '@/pages/ProfilePage';
import { HotelPage } from '@/pages/HotelPage';
import { DayPassPage } from '@/pages/DayPassPage';
import { DayPassConfirmationPage } from '@/pages/DayPassConfirmationPage';
import { SponsorsPage } from '@/pages/SponsorsPage';
import { SponsorsAndVendorsPage } from '@/pages/SponsorsAndVendorsPage';
import { InstructorsPage } from '@/pages/InstructorsPage';
import { RegistrationWizardPage } from '@/features/registration/pages';
import { ConfirmationPage } from '@/pages/ConfirmationPage';
import { OrderHistoryPage } from '@/pages/OrderHistoryPage';
import { OrderDetailPage } from '@/pages/OrderDetailPage';
import { VendorRegistrationPage } from '@/pages/VendorRegistrationPage';

/**
 * Inner routing tree. Exported for testing purposes, so tests can render
 * it with MemoryRouter to verify which routes are wrapped by ClosedRoute.
 */
export const AppRoutes: FC = () => (
  <Routes>
    {/* Public Routes - with main layout */}
    <Route
      path="/*"
      element={
        <Layout>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/events" element={<EventPage />} />
            <Route path="/tournaments/:id" element={<TournamentDetailPage />} />
            <Route path="/activities/:id" element={<ActivityDetailPage />} />
            <Route path="/special-event" element={<SpecialEventPage />} />
            <Route path="/special-events" element={<SpecialEventsPage />} />
            <Route path="/special-events/:slug" element={<SpecialEventDetailPage />} />
            <Route path="/volunteer" element={<VolunteerPage />} />
            <Route path="/hotel" element={<HotelPage />} />
            <Route path="/day-pass" element={<ClosedRoute><DayPassPage /></ClosedRoute>} />
            <Route path="/day-pass/confirmation" element={<DayPassConfirmationPage />} />
            <Route path="/sponsors" element={<SponsorsPage />} />
            <Route path="/sponsors-vendors" element={<SponsorsAndVendorsPage />} />
            <Route path="/instructors" element={<InstructorsPage />} />
            <Route path="/tournaments/:id/register" element={<ClosedRoute><TournamentRegistrationPage /></ClosedRoute>} />
            <Route path="/activities/:id/register" element={<ClosedRoute><ActivityRegistrationPage /></ClosedRoute>} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/event-registration" element={<ClosedRoute><RegistrationWizardPage /></ClosedRoute>} />
            <Route path="/confirmation" element={<ConfirmationPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route path="/orders" element={<OrderHistoryPage />} />
            <Route path="/orders/:id" element={<OrderDetailPage />} />
            <Route path="/vendor-registration/:slug" element={<ClosedRoute><VendorRegistrationPage /></ClosedRoute>} />
          </Routes>
        </Layout>
      }
    />
  </Routes>
);

/**
 * Main App component with routing configuration
 */
export const App: FC = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Analytics />
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
};
